import Box from '@mui/material/Box';
import React, { useContext, useState, useRef } from 'react';
import { palette, spaces } from '../../../../assets/styles/themes/tokens';
import {
  Grow, IconButton,IconButtonProps, MenuItem, Popper, SxProps, Theme
} from '@mui/material';
import MuiCardBaseShort from './MuiCardBaseShort/MuiCardBaseShort';
import MuiCardBaseLong from './MuiCardBaseLong/MuiCardBaseLong';
import getMuiIcon, { IconName } from '../../../../utils/getMuiIcon';
import usePinAction from '../../../../utils/usePinAction';
import { useTranslation } from 'react-i18next';
import { ManageItemInCollectionModalContext } from '../../../../state/ManageItemInCollectionModalProvider';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../reducers/user.selectors';
import {
  getAmpliContentEventPayload,
  getAmpliDeviceId,
  getAmpliSessionId,
  getSearchIdWithFallback,
} from '../../../../utils/ampli';
import services from '../../../../services';
import { CollectionContentSelected, ContentFavoriteAdded, ContentFavoriteRemoved } from '../../../../ampli';
import { MindItem } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import { trackingEvents } from '../../../../services/tracking/trackConfig';
import * as ampliUtils from '../../../../utils/ampli';
import { getDateInGermanFormat } from '../../../../utils/format';
import { Product } from '@kimuno/api-sdk/lib/types/responses/Product';
import { 
  selectIsItemInOwnCollections
} from '../../../../reducers/collections.selectors';
import { colors } from '../../../../assets/styles/themes/variables';
import { routes } from '../../../../constants';
import {
  AmpliContentPayload,
  zIndex,
} from '../../../../constants';
import getTextTruncationSx from '../../../../utils/getTextTruncationSx';
import { selectDocumentsPublishers } from '../../../../reducers/publishers.selectors';
import useScreenSize from '../../../../utils/useScreenSize';
import Menu from '../../../Menu/Menu';
import { Alert } from '../../../NotificationAlert/NotificationAlert';
import { incrementDownloadsCounter as incrementDownloadsCounterAction } from '../../../../actions/user.actions';
import initFileDownload from '../../../utils/downloadIFrame';
import { storeDownloadedItem as storeDownloadedItemAction } from '../../../../actions/download.actions';
import useAction from '../../../../utils/useAction';
import { NotificationContext } from '../../../../state/NotificationContextProvider';
import { notifyBugsnagHandledError } from '../../../../utils/bugsnagClient';

export interface MuiCardCommonProps {
  id: string;
  index?: number;
  isReadOnly: boolean;
  title: string;
  cover: string;
  description: string;
  publisher: string;
  date?: string;
  subjects?: string[];
  classYears?: number[];
  schoolTypes?: string[];
  isPinned: boolean;
  isNew: boolean;
  itemData: Partial<MindItem> | Partial<Product>;
  isFirstElement?: boolean;
  aiMainTopic?: string;
  aiProductItemSubject?: string[];
  aiProductItemClassYears?: string[];
  aiProductItemSchoolType?: string[];
  mindItemPageNumber?: number;
  bestMatchMindItemId?: string;
}

export interface MuiCardBaseProps extends MuiCardCommonProps {
  contentType: 'product' | 'document' | 'video' | 'course';
  chipText?: string;
  link?: {
    to: string;
    label: string;
    icon: IconName;
    hasTooltip: boolean;
  };
  linkFunction?: () => void;
  secondaryLink?: {
    to: string;
    label: string;
    icon: IconName;
    hasTooltip: boolean;
  };
  isNewAddMenuButtonMenu?: boolean;
  onShowNotification?: (alert: Alert) => void;
  isFirstElement: boolean;
}

export const getItemLink = ({
  id, contentType, mindItemPageNumber
}: Pick<MuiCardBaseProps, 'id' | 'contentType' | 'mindItemPageNumber'>
): string => {
  if (contentType === 'product') {
    return `/${routes.productPage.root}/${id}`;
  }

  const path = `/${routes.reader.root}/${id}`;
  return mindItemPageNumber && mindItemPageNumber > 0
    ? `${path}?page=${mindItemPageNumber}`
    : path;
};

export const mapContentTypeName = (contentType: MuiCardBaseProps['contentType']) => {
  switch (contentType) {
    case 'document':
      return 'Unit';
    case 'product':
      return 'Product';
  }

  return contentType;
};

export const  MuiCardBase: React.FC<MuiCardBaseProps> = (props) => {
  const { isXsScreen } =  useScreenSize();
  const component = isXsScreen ? MuiCardBaseShort : MuiCardBaseLong;
  const publishers = useSelector(selectDocumentsPublishers);

  const passedProps = {
    ...props,
    publisher: publishers.find(publisher => publisher.id === props.publisher)?.textValue || props.publisher,
    date: props.date ? getDateInGermanFormat(props.date) : undefined,
  };

  return React.createElement(component, passedProps);
};

export const Metadata: React.FC<{
  sx?: SxProps<Theme>;
  icon: IconName;
  label: string;
  onClick?: (e: React.MouseEvent) => void;
  [key: string]: any;
}> = ({ icon, label, sx, onClick }) => {
  return <Box
    typography='caption'
    sx={{
      ...getTextTruncationSx(),
      wordBreak: 'break-all',
      color: palette.text.secondary,
      ...sx,
    }}
    data-testid='metadata'
    onClick={onClick}
  >
    <Box
      component={getMuiIcon(icon)}
      sx={{
        width: 16,
        height: 16,
        marginRight: spaces.xs,
      }}
      data-testid='metadata__icon'
    />
    {label}
  </Box>;
};

export type PinAction = 'pin' | 'unpin';

export const AddMenuButton: React.FC<{
  index?: MuiCardBaseProps['index'],
  isPinned: MuiCardBaseProps['isPinned'],
  type: MuiCardBaseProps['contentType'],
  id: MuiCardBaseProps['id'],
  itemData: Partial<MindItem> | Partial<Product>,
  isFirstElement?: boolean,
  buttonSx?: SxProps<Theme>;
  size?: IconButtonProps['size'];
  pinActionCallback?: (action: PinAction) => void;
  onClick?: () => void;
  isNewAddMenuButtonMenu?: boolean;
  onShowNotification?: (alert: Alert) => void;
}> = ({
  isPinned,
  type,
  id,
  itemData,
  buttonSx,
  size,
  pinActionCallback,
  onClick,
  isNewAddMenuButtonMenu,
  onShowNotification
}) => {
  const { t } = useTranslation();
  const container = useRef<HTMLButtonElement>(null);
  const [isOpen, setOpen] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const { isXsScreen } = useScreenSize();
  const isInCollection = useSelector(state => selectIsItemInOwnCollections(state, id));

  const openMenu = () => {
    setOpen(true);
    setIsClicked(true);
  };

  const closeMenu = () => {
    setOpen(false);
    setIsClicked(false);
  };

  const iconButtonProperties: IconButtonProps = {
    className: `add-menu-button ${isClicked ? 'active' : ''}`,
    color: 'secondary',
    size: size ? size : (isXsScreen ? 'medium' : 'large'),
    ref: container,
    'aria-label': t('Open item actions'),
    sx: {
      position: 'absolute',
      '&.active': {
        backgroundColor: colors.gray50,
        borderRadius: spaces.xs,
      },
      top: isXsScreen ? `-${spaces.xs}` : 'unset',
      right: isXsScreen ? `-${spaces.xs}` : `-${spaces.sm}`,
      color: palette.text.secondary,
      '& svg': {
        width: '24px !important',
        height: '24px !important'
      },
      ...buttonSx,
    },
    'aria-haspopup': 'true',
    'aria-expanded': isOpen ? 'true' : undefined,
    onClick: () => {
      onClick && onClick();
      if (!isOpen) {
        openMenu();
      }
    },  
    //@ts-ignore
    'data-testid': 'add-menu-button',
  };

  const iconProperties = {
    className: 'add-menu-button__icon',
    component: isNewAddMenuButtonMenu 
      ? getMuiIcon('more')
      : (isPinned || isInCollection)
        ? getMuiIcon('bookmarkAdded')
        : getMuiIcon('bookmark')
  };

  return <>
    <IconButton {...iconButtonProperties}>
      <Box {...iconProperties} sx={{ cursor: 'pointer' }} />
    </IconButton>
    <Popper
      // TODO FD-3300: add partial data here for tracking on click
      open={isOpen}
      anchorEl={container.current}
      data-testid='add-menu'
      placement='bottom-end'
      transition
      sx={{
        zIndex: `${zIndex.addMenu} !important`,
      }}
    >
      {({ TransitionProps }) => (
        <Grow
          {...TransitionProps}
        >
          <Box>
            <AddMenu
              onClose={closeMenu}
              isOpen={isOpen}
              type={type}
              id={id}
              isPinned={isPinned}
              pinActionCallback={pinActionCallback}
              itemData={itemData}
              isNewAddMenuButtonMenu={isNewAddMenuButtonMenu}
              onShowNotification={onShowNotification}
            />
          </Box>
        </Grow>
      )}
    </Popper>
  </>;
};

export const AddMenu: React.FC<{
  onClose: () => void;
  isOpen: boolean;
  type: MuiCardBaseProps['contentType'],
  id: string,
  isPinned: boolean,
  pinActionCallback?: (action: PinAction) => void;
  itemData: Partial<MindItem> | Partial<Product>,
  isNewAddMenuButtonMenu?: boolean,
  onShowNotification?: (alert: Alert) => void;
}> = (props) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const storeDownloadedItem = useAction(storeDownloadedItemAction);
  const incrementDownloadsCounter = useAction(incrementDownloadsCounterAction);
  const {
    show: showManageItemInCollectionsModal,
  } = useContext(ManageItemInCollectionModalContext);
  const { showNotification } = useContext(NotificationContext);

  const variation = props.type === 'product' ? 'product' : 'mindItem';
  const pinAction = usePinAction(props.id, props.isPinned, variation);
  const pinIcon = props.isPinned ? 'starFilledPurple' : 'starPurple';
  const pinText = props.isPinned ? t('Unpin') : t('Pin');
  const pinTestId = props.isPinned ? 'new-unpin-button' : 'new-pin-button';

  const isInCollection = useSelector(state => selectIsItemInOwnCollections(state, props.id));
  const collectionIcon = isInCollection ? 'folderRemovePurple' : 'folderAddPurple';
  const collectionText = isInCollection ? t('Remove from collection') : t('Add to collection');

  const isDownloadable =
    'type' in props.itemData &&
    props.itemData.type === 'mindItem' &&
    'fileType' in props.itemData &&
    props.itemData.fileType === 'document' &&
    'isReadOnly' in props.itemData &&
    !props.itemData.isReadOnly;

  const getMenuItem = (icon: IconName, label: string, testId: string, onClick: () => void) => {
    return <MenuItem
      data-testid={testId}
      onClick={onClick}
    >
      <Box
        data-testid='add-menu__item__icon'
        component={getMuiIcon(icon)}
      />
      {label}
    </MenuItem>;
  };

  const processCollectionClick = () => {
    props.onClose();

    showManageItemInCollectionsModal(props.id, variation, props.itemData, undefined, props.onShowNotification);

    if (user && props.itemData) {
      const eventProperties = getAmpliContentEventPayload(props.itemData, variation);
      const collectionContentSelectedPayload = {
        content: eventProperties.content,
      };
      services.track.ampliEventTrack({
        event: new CollectionContentSelected(collectionContentSelectedPayload as any)
      });
    }
  };

  const processPinAction = () => {
    pinAction()?.then(() => {
      if (props.pinActionCallback) props.pinActionCallback(props.isPinned ? 'unpin' : 'pin');
      
      if (props.onShowNotification) {
        props.onShowNotification({
          message: props.isPinned ? 
            t('The material was successfully removed from favorites.')
            : t('The material has been successfully added to the favorites.'),
          severity: 'success'
        });
      }
    }).catch(() => {
      if (props.onShowNotification) {
        props.onShowNotification({
          message: t('Something went wrong. Check your internet connection and try again.'),
          severity: 'error'
        });
      }
    });
    
    props.onClose();

    let event, eventPayload, eventProperties: AmpliContentPayload | undefined;
    if (variation === 'mindItem') {
      event = trackingEvents.mindItemPin;
      eventPayload = { 'minditem_id': props.itemData.id };
      eventProperties = props.itemData && ampliUtils.getAmpliContentEventPayload(props.itemData, 'mindItem');
    } else if (variation === 'product') {
      event = trackingEvents.productPin;
      eventPayload = { 'product_id': props.itemData.id };
      eventProperties = props.itemData && ampliUtils.getAmpliContentEventPayload(props.itemData, 'product');
    }

    if (event) {
      services.track.eventTrack(event, { other: { ...eventPayload, isPinned: !props.isPinned } });
    }

    const searchId = getSearchIdWithFallback();
    const ampliAction = !props.isPinned ? ContentFavoriteAdded : ContentFavoriteRemoved;

    if (user && eventProperties) {
      if (searchId && !props.isPinned) {
        (eventProperties as any)['search id'] = searchId;
      }
      services.track.ampliEventTrack({
        event: new ampliAction(eventProperties as any)
      });
    }
  };

  const processDownloadAction = async () => {
    props.onClose();
    const trackingInfo = {
      sessionId: getAmpliSessionId(),
      deviceId: getAmpliDeviceId(),
    };

    try {
      const downloadInfo = await services.mindItem.getDownloadInformation(props.id, trackingInfo);
      const { downloadLimit, counter, mainDocument, canDownload } = downloadInfo;
      const leftDownloads = downloadLimit - counter - (mainDocument?.countsTowardsLimit ? 1 : 0);

      if (leftDownloads < 0) {
        showNotification?.({
          message: t('Your download limit for this month has been reached. '
      + 'Starting next month you will be able to download new materials again.'),
          severity: 'warning',
        });
      } else if (canDownload && mainDocument?.canDownload && mainDocument?.link) {
        initFileDownload(mainDocument.link);
        storeDownloadedItem(mainDocument.id);
        if (mainDocument.countsTowardsLimit) incrementDownloadsCounter();

        showNotification?.({
          message: t('You still have <number> of <limit> downloads left this month',
            { number: leftDownloads, limit: downloadLimit }),
          severity: 'success',
        });
      }
    } catch (error) {
      notifyBugsnagHandledError(error as Error);
      showNotification?.({
        message: t('Something went wrong. Please try again later.'),
        severity: 'error',
      });
    }
  };

  return <Menu
    isOpen={props.isOpen}
    paperSx={{
      overflow: 'hidden',
      transformOrigin: 'top right',
      marginTop: spaces.xxs,
    }}
    onClose={props.onClose}
  >
    {getMenuItem(pinIcon, pinText, pinTestId, processPinAction)}
    {getMenuItem(collectionIcon, collectionText, 'new-add-to-collection-button', processCollectionClick)}
    {
      props.isNewAddMenuButtonMenu && 
      isDownloadable && 
      getMenuItem('download', t('Download'), 'download-button', processDownloadAction)
    }
  </Menu>;
};

export default MuiCardBase;
